import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("auth_session_expired") === "1") {
        sessionStorage.removeItem("auth_session_expired");
        toast.error("Your session has expired. Please sign in again.");
        setError("Your session has expired. Please sign in again.");
      }
    } catch {
      // sessionStorage unavailable — ignore silently.
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email, password);
      const fallback = user.role === "admin" ? "/admin" : "/captain";
      // Only honour same-site paths to prevent open-redirect via //evil.com.
      const isSafeNext =
        typeof nextPath === "string" &&
        nextPath.startsWith("/") &&
        !nextPath.startsWith("//");
      const dest = isSafeNext ? nextPath : fallback;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="login-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Sign in</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tighter mt-3">Welcome back.</h1>
      <p className="mt-2 text-muted-foreground">For admins and approved team captains.</p>

      <form onSubmit={submit} className="mt-10 space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        </div>
        {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
        <Button type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="mt-8 text-sm text-muted-foreground">
        New captain? <Link to="/register" className="text-primary underline">Register here</Link>.
      </div>
    </div>
  );
}
